/**
 * ReviewList — Premium review display with filtering and sorting
 */
import React, { useState, useMemo } from 'react';
import { ChevronDown, Filter, PenSquare } from 'lucide-react';
import { Rating, RatingSummary, Button } from '../../shared';
import ReviewCard from './ReviewCard';
import { calculateReviewStats } from '../../../data/reviews';

const SORT_OPTIONS = {
  recent: { en: 'Most Recent', th: 'ล่าสุด' },
  helpful: { en: 'Most Helpful', th: 'เป็นประโยชน์มากที่สุด' },
  highest: { en: 'Highest Rated', th: 'คะแนนสูงสุด' },
  lowest: { en: 'Lowest Rated', th: 'คะแนนต่ำสุด' }
};

const FILTER_OPTIONS = [5, 4, 3, 2, 1];

export function ReviewList({
  restaurantId,
  reviews = [],
  language = 'th',
  onWriteReview,
  onHelpful,
  onReport,
  className = ''
}) {
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  const [showSortDropdown, setShowSortDropdown] = useState(false);

  // Calculate stats
  const stats = useMemo(() => calculateReviewStats(reviews), [reviews]);

  // Filter and sort reviews
  const filteredReviews = useMemo(() => {
    let filtered = [...reviews];

    // Apply star filter
    if (filter !== 'all') {
      filtered = filtered.filter(r => r.rating === parseInt(filter));
    }

    // Apply sort
    switch (sortBy) {
      case 'recent':
        filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
      case 'helpful':
        filtered.sort((a, b) => (b.helpful || 0) - (a.helpful || 0));
        break;
      case 'highest':
        filtered.sort((a, b) => b.rating - a.rating);
        break;
      case 'lowest':
        filtered.sort((a, b) => a.rating - b.rating);
        break;
    }

    return filtered;
  }, [reviews, filter, sortBy]);

  const getFilterCount = (star) => {
    return reviews.filter(r => r.rating === star).length;
  };

  return (
    <div className={className}>
      {/* Rating Summary */}
      {reviews.length > 0 && (
        <div className="bg-white border border-neutral-100 rounded-2xl p-6 mb-6">
          <RatingSummary
            averageRating={stats.averageRating}
            totalReviews={stats.totalReviews}
            distribution={stats.ratingDistribution}
          />

          {/* Category averages */}
          {Object.values(stats.categoryAverages).some(v => v > 0) && (
            <div className="mt-6 pt-6 border-t border-neutral-100">
              <div className="grid grid-cols-4 gap-4">
                {Object.entries(stats.categoryAverages).map(([key, value]) => {
                  if (!value) return null;
                  const labels = {
                    food: language === 'th' ? 'อาหาร' : 'Food',
                    service: language === 'th' ? 'บริการ' : 'Service',
                    atmosphere: language === 'th' ? 'บรรยากาศ' : 'Atmosphere',
                    value: language === 'th' ? 'ความคุ้มค่า' : 'Value'
                  };
                  return (
                    <div key={key} className="text-center">
                      <div className="text-[20px] font-semibold text-neutral-900">
                        {value.toFixed(1)}
                      </div>
                      <div className="text-[12px] text-neutral-500 mt-1">
                        {labels[key]}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Header with Write Review button */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[16px] font-semibold text-neutral-900">
          {language === 'th' ? 'รีวิวทั้งหมด' : 'All Reviews'}
          <span className="text-neutral-400 font-normal ml-2">
            ({stats.totalReviews})
          </span>
        </h3>
        {onWriteReview && (
          <Button
            variant="primary"
            size="sm"
            icon={<PenSquare size={14} />}
            onClick={onWriteReview}
          >
            {language === 'th' ? 'เขียนรีวิว' : 'Write Review'}
          </Button>
        )}
      </div>

      {/* Filters and Sort */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        {/* Star Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-1.5 rounded-full text-[13px] font-medium transition-colors ${
              filter === 'all'
                ? 'bg-neutral-900 text-white'
                : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
            }`}
          >
            {language === 'th' ? 'ทั้งหมด' : 'All'}
          </button>
          {FILTER_OPTIONS.map(star => (
            <button
              key={star}
              onClick={() => setFilter(filter === star ? 'all' : star)}
              className={`px-3 py-1.5 rounded-full text-[13px] font-medium transition-colors ${
                filter === star
                  ? 'bg-gold-500 text-white'
                  : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
              }`}
            >
              {star} ★
              <span className="ml-1 text-neutral-400">
                ({getFilterCount(star)})
              </span>
            </button>
          ))}
        </div>

        {/* Sort Dropdown */}
        <div className="relative">
          <button
            onClick={() => setShowSortDropdown(!showSortDropdown)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-neutral-200 text-[13px] text-neutral-700 hover:bg-neutral-50 transition-colors"
          >
            <Filter size={14} />
            {SORT_OPTIONS[sortBy]?.[language] || SORT_OPTIONS[sortBy]?.en}
            <ChevronDown size={14} className={`transition-transform ${showSortDropdown ? 'rotate-180' : ''}`} />
          </button>

          {showSortDropdown && (
            <div className="absolute right-0 top-full mt-2 w-48 bg-white border border-neutral-100 rounded-xl shadow-lg overflow-hidden z-10">
              {Object.entries(SORT_OPTIONS).map(([key, labels]) => (
                <button
                  key={key}
                  onClick={() => {
                    setSortBy(key);
                    setShowSortDropdown(false);
                  }}
                  className={`w-full px-4 py-2.5 text-left text-[13px] ${
                    sortBy === key
                      ? 'bg-brand-50 text-brand-600 font-medium'
                      : 'text-neutral-700 hover:bg-neutral-50'
                  }`}
                >
                  {labels[language] || labels.en}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Reviews List */}
      <div className="space-y-4">
        {filteredReviews.length === 0 ? (
          <div className="text-center py-12 bg-white border border-neutral-100 rounded-2xl">
            <div className="text-neutral-400 mb-2">
              {language === 'th' ? 'ไม่มีรีวิว' : 'No reviews yet'}
            </div>
            {onWriteReview && (
              <Button
                variant="secondary"
                size="sm"
                onClick={onWriteReview}
              >
                {language === 'th' ? 'เป็นคนแรกที่รีวิว' : 'Be the first to review'}
              </Button>
            )}
          </div>
        ) : (
          filteredReviews.map(review => (
            <ReviewCard
              key={review.id}
              review={review}
              language={language}
              onHelpful={onHelpful}
              onReport={onReport}
            />
          ))
        )}
      </div>

      {/* Load More */}
      {filteredReviews.length >= 10 && (
        <div className="text-center mt-6">
          <Button variant="secondary">
            {language === 'th' ? 'โหลดเพิ่มเติม' : 'Load More'}
          </Button>
        </div>
      )}
    </div>
  );
}

export default ReviewList;
